<template>
  <div class="stages">
    <div v-if="stages.length > 1" class="stages__header">
      <div class="stages__wrapper">
        <div
          :class="[
            {
              '--complete': isStageComplete(stage),
              '--active': currentStage && currentStage.id === stage.id
            },
            'stages__stage'
          ]"
          v-for="stage in stages"
          :key="stage.id"
          @click="() => (currentStage = stage)"
        >
          <h6 class="stages__stage-title">{{ stageTitle }}</h6>
          <span>{{ stage.position }}</span>
        </div>
        <div class="horz-spacer"></div>
      </div>
      <v-ons-progress-bar :value="programProgress" secondary="100" />
    </div>

    <div :class="[{ '--single': stages.length <= 1 }, 'stages__content']">
      <template v-if="currentStage && currentStage.enabled">
        <stream-template
          no-toolbar
          :blocks="currentStage.stream.blocks"
          :unlock="isUserSubscribed"
        />

        <template v-if="isStageComplete(currentStage)">
          <v-button @click="markStageUnDone" block secondary ghost>
            {{ $t("components.program.stages.uncompleted") }} {{ stageTitle }}
          </v-button>
        </template>
      </template>

      <template v-else-if="currentStage && !currentStage.enabled">
        <p class="text-center mt-5 lead">
          <v-icon icon="lock" size="3rem" />
          <span class="d-block mt-3">
            {{ $t("components.program.stages.uncompleted", { stageTitle: stageTitle }) }}
          </span>
        </p>
      </template>

      <template v-else>
        <v-loader />
      </template>
    </div>

    <cta-row v-if="isUserSubscribed && !isStageComplete(currentStage)">
      <v-button rounded raised :is-loading="isLoading" @click="markStageAsDone">
        {{ $t("components.program.stages.markComplete") }}
        <v-icon class="ml-1" icon="done_all" size="1.5rem" />
      </v-button>
    </cta-row>

    <alert-dialog
      :title="
        $t('components.program.stages.dialogCompleteTitle', {
          stageTitle: capitalizeFirstLetter(stageTitle)
        })
      "
      :actions="[
        {
          title: $t('components.program.stages.dialogCompleteGoNext', {
            stageTitle: capitalizeFirstLetter(stageTitle)
          }),
          callback: goToNextStage
        }
      ]"
      :show="showStageCompleteDialog"
      @close="() => (showStageCompleteDialog = false)"
      centered
    >
      <v-icon icon="verified_user" size="4rem" color="var(--brand-color-hex)" />
    </alert-dialog>

    <alert-dialog
      :title="$t('components.program.stages.dialogProgramCompleteTitle')"
      :actions="[
        {
          title: $t('components.program.stages.dialogDismiss'),
          callback: () => closeCompleteDialog()
        }
      ]"
      :show="showProgramCompleteDialog"
      @close="() => closeCompleteDialog()"
      centered
    >
      <v-icon icon="done_all" size="4rem" color="var(--brand-color-hex)" />
    </alert-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"

import { capitalizeFirstLetter } from "@/lib/utils"

import CtaRow from "@/components/CtaRow"
import AlertDialog from "@/components/AlertDialog"
import StreamTemplate from "@/components/page-templates/StreamTemplate"

export default {
  props: {
    programId: { type: Number, required: true },
    programTitle: { type: String, required: true },
    stageTitle: { type: String, required: true },
    stages: { type: Array, required: true }
  },

  components: {
    CtaRow,
    AlertDialog,
    StreamTemplate
  },

  data() {
    return {
      isLoading: false,
      currentStage: null,
      showStageCompleteDialog: false,
      showProgramCompleteDialog: false
    }
  },

  methods: {
    capitalizeFirstLetter,

    ...mapActions("programs", ["completeStage", "uncompleteStage", "complete"]),
    ...mapActions("analytics", ["trackActivity"]),

    markStageAsDone() {
      this.isLoading = true

      this.completeStage({
        programId: this.programId,
        stageId: this.currentStage.id
      })
        .then(() => {
          if (this.activeStage.id === this.currentStage.id) {
            this.complete(this.programId)
            this.showProgramCompleteDialog = true
          } else {
            this.showStageCompleteDialog = true
          }
        })
        .catch(() => {
          this.$notify(this.$t("components.program.stages.error"))
        })
        .finally(() => (this.isLoading = false))
    },

    markStageUnDone() {
      this.uncompleteStage({
        programId: this.programId,
        stageId: this.currentStage.id
      }).catch(() => {
        this.$notify(this.$t("components.program.stages.error"))
      })
    },

    isStageComplete(stage) {
      return stage && this.isUserSubscribed && this.completedStages[stage.id]
    },

    goToNextStage() {
      this.showStageCompleteDialog = false
      this.currentStage = this.activeStage
    },

    closeCompleteDialog() {
      this.showProgramCompleteDialog = false
      this.$emit("closeCompleteDialog")
    }
  },

  computed: {
    ...mapGetters("programs", [
      "completedStagesForProgram",
      "activeStageForProgram",
      "isUserSubscribedToProgram"
    ]),

    activeStage() {
      return this.activeStageForProgram(this.programId, this.stages)
    },

    completedStages() {
      return this.completedStagesForProgram(this.programId)
    },

    programProgress() {
      return Math.floor(
        (Object.keys(this.completedStages).length / this.stages.length) * 100
      )
    },

    isUserSubscribed() {
      return this.isUserSubscribedToProgram(this.programId)
    }
  },

  watch: {
    isUserSubscribed: {
      immediate: true,
      handler: function(isUserSubscribed) {
        if (isUserSubscribed) {
          this.currentStage = this.activeStage
        } else {
          this.currentStage = this.stages[0]
        }
      }
    },

    currentStage: {
      immediate: true,
      handler() {
        if (this.currentStage) {
          this.trackActivity({
            activityType: "program_stage_viewed",
            data: {
              program_id: this.programId,
              program_title: this.programTitle,
              program_stage_id: this.currentStage.id,
              program_stage_title: `${capitalizeFirstLetter(this.stageTitle)} ${
                this.currentStage.position
              }`
            }
          })
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.stages__header {
  box-shadow: $box-shadow--medium;
}

.stages__content {
  &.--single {
    background: $color-ui--white;
  }
}

.stages__wrapper {
  display: flex;
  background: $color-ui--white;
  overflow-x: scroll;
  overflow-y: hidden;
  padding: $size--2 $size--4 $size--4;
}

.stages__stage {
  min-height: $size--9;
  min-width: $size--10;
  border-radius: var(--border-radius);
  background: $color-ui--grey-10;
  margin-right: $size--2;
  padding: $size--3;
  color: $color-ui--grey-70;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: $font-weight--bold;
  transition: all 0.2s ease;

  &.--complete {
    background: rgba(var(--brand-color-rgb-vals), 0.2);
    color: var(--brand-color-hex);
    border-color: var(--brand-color-hex);
  }

  &.--active {
    background: var(--brand-color-hex);
    color: $color-ui--white;
    border-color: var(--brand-color-hex);
  }
}

.stages__stage-title {
  font-size: 10px;
  margin: 0 0 $size--1;
  color: inherit;
  letter-spacing: 0.5px;
}
</style>

<style lang="scss">
.stages__header {
  .progress-bar--material__primary {
    background-color: var(--brand-color-hex);
  }
}
</style>
